import React, { useState } from 'react';
import Hero from '../components/Hero';
import FeaturedProducts from '../components/FeaturedProducts';
import BiggestSavings from '../components/BiggestSavings';
import HowItWorks from '../components/HowItWorks';
import PopularProducts from '../components/PopularProducts';
import BrandsCarousel from '../components/BrandsCarousel';
import Services from '../components/Services';
import Testimonials from '../components/Testimonials';
import BlogSection from '../components/BlogSection';
import OccasionReparateurs from '../components/OccasionReparateurs';
import SeoIntro from '../components/SeoIntro';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useJsonLd } from '../hooks/useSEO';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { SiteConfigAPI } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, Pencil, Check } from 'lucide-react';

const SECTION_LABELS = {
  occasion: 'Occasion & Réparateurs',
  featured: 'Produits à la une',
  savings: 'Meilleures économies',
  howitworks: 'Comment ça marche',
  popular: 'Produits populaires',
  brands: 'Marques',
  services: 'Services',
  testimonials: 'Témoignages',
  blog: 'Blog',
};

const SortableSection = ({ id, editMode, hidden, onToggle, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editMode });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  if (!editMode) {
    return hidden ? null : <>{children}</>;
  }
  return (
    <div ref={setNodeRef} style={style} className="relative border-2 border-dashed border-[#1F4D3F]/30 rounded-xl my-2 mx-2">
      <div className="absolute top-2 right-2 z-20 flex items-center gap-2 bg-white shadow rounded-lg px-2 py-1">
        <span className="text-xs font-medium text-[#1F4D3F]">{SECTION_LABELS[id] || id}</span>
        <button onClick={() => onToggle(id)} className="text-[#7A6F5C] hover:text-[#1F4D3F]" title={hidden ? 'Afficher' : 'Masquer'}>
          {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-[#1F4D3F]" title="Glisser pour déplacer">
          <GripVertical className="w-4 h-4" />
        </button>
      </div>
      <div className={hidden ? 'opacity-40 pointer-events-none' : ''}>{children}</div>
    </div>
  );
};

const Home = ({ onAddToCart }) => {
  const { user } = useAuth();
  const { config, refresh } = useSiteConfig();
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  useDocumentMeta({
    title: 'Matériel Dentaire Algérie — Comparez les Prix | Comptoir Online',
    description: 'Comptoir Online, la marketplace du matériel dentaire en Algérie. Comparez les prix de tous les fournisseurs, commandez en ligne, livraison rapide. Implants, consommables, équipements.',
  });
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Comptoir Online',
    url: 'https://comptoir-online.com',
  });

  const isAdmin = user && user.role === 'admin';
  const DEFAULT_ORDER = ['occasion','featured','savings','howitworks','popular','brands','services','testimonials','blog'];
  const order = Array.isArray(config['home.sections_order']) && config['home.sections_order'].length
    ? config['home.sections_order'] : DEFAULT_ORDER;
  const hidden = Array.isArray(config['home.sections_hidden']) ? config['home.sections_hidden'] : [];

  const sectionComp = (id) => {
    switch (id) {
      case 'occasion': return <OccasionReparateurs editMode={editMode} />;
      case 'featured': return <FeaturedProducts onAddToCart={onAddToCart} editMode={editMode} />;
      case 'savings': return <BiggestSavings />;
      case 'howitworks': return <HowItWorks />;
      case 'popular': return <PopularProducts onAddToCart={onAddToCart} />;
      case 'brands': return <BrandsCarousel />;
      case 'services': return <Services />;
      case 'testimonials': return <Testimonials />;
      case 'blog': return <BlogSection />;
      default: return null;
    }
  };

  const saveOrder = async (newOrder, newHidden) => {
    try {
      const full = await SiteConfigAPI.adminGet();
      full['home.sections_order'] = newOrder;
      full['home.sections_hidden'] = newHidden !== undefined ? newHidden : hidden;
      await SiteConfigAPI.adminSet(full);
      await refresh();
      toast({ title: 'Page d accueil mise à jour' });
    } catch (e) { toast({ title: 'Erreur', description: 'Echec de l enregistrement' }); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveOrder(arrayMove(order, oldIndex, newIndex));
  };

  const toggleHidden = (id) => {
    const newHidden = hidden.includes(id) ? hidden.filter(x => x !== id) : [...hidden, id];
    saveOrder(order, newHidden);
  };

  return (
    <>
      {isAdmin && (
        <div className="bg-[#1F4D3F] text-white text-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <span className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Mode admin — page d'accueil</span>
            <button onClick={() => setEditMode(!editMode)} className={`px-3 py-1.5 rounded-lg font-medium flex items-center gap-1 ${editMode ? 'bg-white text-[#1F4D3F]' : 'bg-white/20 hover:bg-white/30'}`}>
              {editMode ? <><Check className="w-4 h-4" /> Édition activée</> : 'Réorganiser les sections'}
            </button>
          </div>
        </div>
      )}

      <Hero editMode={editMode} />

      {editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((id) => (
              <SortableSection key={id} id={id} editMode={editMode} hidden={hidden.includes(id)} onToggle={toggleHidden}>
                {sectionComp(id)}
              </SortableSection>
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        order.filter(id => !hidden.includes(id)).map((id) => (
          <React.Fragment key={id}>{sectionComp(id)}</React.Fragment>
        ))
      )}
      <SeoIntro />
    </>
  );
};

export default Home;
